import { fileHref } from '../lib/fileAccess'
import { useEffect, useState, useMemo, useRef } from 'react'
import { fetchRows, createRow, updateRow } from '../lib/api'
import { useAuth } from '../hooks/useAuth'
import { shipTodayOr, shipNowOr } from '../lib/shipTime'
import { supabase } from '../lib/supabase'
import html2pdf from 'html2pdf.js'
import { filePath } from '../lib/filePaths'
import HistoryLink from '../components/HistoryLink'
import { matchHorRequest } from '../lib/horRequest'
import { horMasterState } from '../lib/permModules'
import { evaluateDay, buildTimeline, evaluateRolling24, rollingSevenDayRest } from '../lib/horRules'

// ============================================================
// CONSTANTS
// ============================================================
// 48 half-hour blocks per day. Index 0 = 00:00-00:30, index 47 = 23:30-24:00.
// Block value: 1 = WORK, 0 = REST. Default all rest (0).
const BLOCKS_PER_DAY = 48

const LEAVE_TYPES = [
  'Onboard',
  'Compassionate Leave', 'Unpaid Leave', 'Off Rotation', 'On Vacation',
  'Shoreside Training', 'Medical Leave', 'Yacht Business Leave', 'Not Aboard',
  'Paid Time Off (No Leave Used)', 'Paid Travel Days', 'Neutral Travel Days',
  'Unpaid Travel Days', 'Paid Quarantine Days', 'Neutral Quarantine Days', 'Unpaid Quarantine Days',
]

// ============================================================
// HELPERS
// ============================================================
function getVal(v) {
  if (v == null) return ''
  if (typeof v === 'object') return v.value != null ? v.value : ''
  return v
}

function emptyDay() {
  return { blocks: new Array(BLOCKS_PER_DAY).fill(0), comment: '', status: 'Onboard' }
}

function daysInMonth(year, month) { // month 1-12
  return new Date(year, month, 0).getDate()
}

function blockToLabel(i) {
  const h = Math.floor(i / 2)
  const m = i % 2 === 0 ? '00' : '30'
  return String(h).padStart(2, '0') + ':' + m
}

// hours of work and rest for a single day's block array
function dayHours(blocks) {
  const workBlocks = blocks.reduce((a, b) => a + (b ? 1 : 0), 0)
  const workHours = workBlocks * 0.5
  return { work: workHours, rest: 24 - workHours }
}

// ============================================================
// COMPONENT
// ============================================================
export default function HoursOfRest({ onNavigate }) {
  const { can } = useAuth()
  const { config } = useAuth()
  const [crewList, setCrewList] = useState([])
  const [selectedCrew, setSelectedCrew] = useState(null)
  const [month, setMonth] = useState(() => {
    const d = new Date()
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
  })
  // Once config arrives, snap the default month to SHIP time (only if the user
  // hasn't navigated away from the browser-guessed month, and only if it differs).
  const monthTouched = useRef(false)
  useEffect(() => {
    if (monthTouched.current || !config?.timezone) return
    const shipMonth = shipTodayOr(config.timezone).slice(0, 7)
    setMonth(m => (m === shipMonth ? m : shipMonth))
  }, [config?.timezone])
  const [record, setRecord] = useState(null) // { rowId, days: { 'YYYY-MM-DD': day } }
  const [activeDate, setActiveDate] = useState(null)
  const [schedules, setSchedules] = useState([])

  useEffect(() => {
    if (!config?.id) return
    supabase.from('watch_schedules').select('name,date_from,date_to,grid')
      .eq('client_id', config.id)
      .then(({ data }) => setSchedules(data || []))
  }, [config?.id])

  // The crew member's PLANNED pattern for the active date, if a roster covers it.
  // Reference only - never written to the record unless the seafarer says so.
  const rosterForDate = (() => {
    if (!activeDate || !selectedCrew) return null
    const name = (getVal(selectedCrew['First Name']) + ' ' + getVal(selectedCrew['Last Name'])).trim().toLowerCase()
    for (const sc of schedules) {
      if (sc.date_from && activeDate < sc.date_from) continue
      if (sc.date_to && activeDate > sc.date_to) continue
      const row = (sc.grid || []).find(r => (r.crew_name || '').trim().toLowerCase() === name)
      if (row && typeof row.cells === 'string' && row.cells.length === 24) {
        return { schedule: sc.name, role: row.role, cells: row.cells }
      }
    }
    return null
  })()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [aiText, setAiText] = useState('')
  const [aiBusy, setAiBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [showReport, setShowReport] = useState(false)
  const [showArchive, setShowArchive] = useState(false)
  const [archiveRows, setArchiveRows] = useState([])
  const [archiveLoading, setArchiveLoading] = useState(false)

  const [year, mon] = month.split('-').map(Number)
  const numDays = daysInMonth(year, mon)

  // build list of dates in the month
  const dates = useMemo(() => {
    const arr = []
    for (let d = 1; d <= numDays; d++) {
      arr.push(`${year}-${String(mon).padStart(2, '0')}-${String(d).padStart(2, '0')}`)
    }
    return arr
  }, [year, mon, numDays])

  // load crew on mount
  useEffect(() => {
    (async () => {
      try {
        let list = await fetchRows('crew').catch(() => []) || []
        // Crew logins see only their own hours - even when the Crew module is hidden for them.
        if (config?.accountType === 'crew') {
          const me = String(config?.crewName || '').toLowerCase().split(/\s+/).filter(Boolean)
          const hasMe = n => me.length > 0 && me.every(t => String(n || '').toLowerCase().includes(t))
          const inCrew = list.filter(c => hasMe(getVal(c['First Name']) + ' ' + getVal(c['Last Name'])))
          if (inCrew.length) list = inCrew
          else {
            const hr = await fetchRows('hours_rest').catch(() => []) || []
            const names = [...new Set(hr.map(r => getVal(r['Crew Member'])).filter(Boolean))]
            const full = names.find(hasMe) || config?.crewName || ''
            const parts = full.trim().split(/\s+/)
            list = [{ id: 'self', 'First Name': parts[0] || '', 'Last Name': parts.slice(1).join(' '), 'Position': config?.role || '' }]
          }
        }
        setCrewList(list)
        if (config?.accountType === 'crew' && list.length === 1) setSelectedCrew(list[0])
      } catch (e) { setMsg('Failed to load crew') }
    })()
  }, [config?.id])

  // when crew + month chosen, load or init the record
  useEffect(() => {
    if (!selectedCrew) return
    loadRecord()
  }, [selectedCrew, month])

  // the AI asked for a crew member's report ("Jamie Rowan, 2026-10, report, archive"): pick them + the month,
  // open the report and (when asked) run the same Save to Archive as the button
  const [aiReport, setAiReport] = useState(null)   // { name, month, archive }
  useEffect(() => {
    if (!crewList.length) return
    let raw = null
    try { raw = localStorage.getItem('hc_hor_report'); localStorage.removeItem('hc_hor_report') } catch (e) {}
    const m = raw ? matchHorRequest(raw, crewList) : null
    if (!m) return
    if (m.error) { setMsg(m.error); return }
    monthTouched.current = true
    setMonth(m.month)
    setSelectedCrew(m.crew)
    setAiReport({ name: getVal(m.crew['First Name']) + ' ' + getVal(m.crew['Last Name']), month: m.month, archive: m.archive })
  }, [crewList])
  // ...once THAT month of THAT crew member has loaded
  useEffect(() => {
    if (!aiReport || loading || !record || record.month !== aiReport.month || record.crewName !== aiReport.name) return
    setShowReport(aiReport.archive ? 'archive' : true)
    setAiReport(null)
  }, [aiReport, record, loading])

  async function loadRecord() {
    setLoading(true); setMsg('')
    try {
      const rows = await fetchRows('hours_rest')
      const crewName = getVal(selectedCrew['First Name']) + ' ' + getVal(selectedCrew['Last Name'])
      const forCrew = (rows || []).filter(r => getVal(r['Crew Member']) === crewName)

      const existing = forCrew.find(r => getVal(r['Month']) === month)
      let days = {}
      if (existing) {
        try { days = JSON.parse(getVal(existing['Days']) || '{}') } catch { days = {} }
      }
      // ensure every date in THIS month has a day object
      dates.forEach(d => { if (!days[d]) days[d] = emptyDay() })

      // Merge in adjacent-month boundary days so rolling 24h / 7-day windows compute at month edges.
      // Previous month (need its last ~7 days), next month (need its first ~2 days).
      const prevMonth = (() => { const d = new Date(year, mon - 2, 1); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') })()
      const nextMonth = (() => { const d = new Date(year, mon, 1); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') })()
      const mergeFrom = (monthKey) => {
        const rec = forCrew.find(r => getVal(r['Month']) === monthKey)
        if (!rec) return
        let pd = {}
        try { pd = JSON.parse(getVal(rec['Days']) || '{}') } catch { return }
        Object.keys(pd).forEach(k => { if (!days[k]) days[k] = pd[k] })
      }
      mergeFrom(prevMonth)
      mergeFrom(nextMonth)

      setRecord({ rowId: existing ? existing.id : null, days, crewName, month })
      setActiveDate(dates[0])
    } catch (e) {
      setMsg('Failed to load record: ' + e.message)
    } finally { setLoading(false) }
  }

  async function openArchive() {
    setShowArchive(true); setArchiveLoading(true)
    try {
      const rows = await fetchRows('hours_rest')
      const withReports = (rows || []).filter(r => getVal(r['Report URL']))
      setArchiveRows(withReports)
    } catch (e) { setArchiveRows([]) }
    finally { setArchiveLoading(false) }
  }

  const day = record && activeDate ? record.days[activeDate] : null

  function toggleBlock(i) {
    if (!day || day.status !== 'Onboard') return
    const blocks = [...day.blocks]
    blocks[i] = blocks[i] ? 0 : 1
    updateDay({ ...day, blocks })
  }

  function updateDay(newDay) {
    // Stamp the day with the vessel timezone in force when it was recorded.
    // Additive: legacy days without a stamp remain valid. If the Master changes
    // the ship's zone mid-month, differing stamps make the record say so.
    const tz = config?.timezone || null
    const stamped = tz ? { ...newDay, tz } : newDay
    setRecord(r => ({ ...r, days: { ...r.days, [activeDate]: stamped } }))
  }

  function clearDay() {
    updateDay({ ...day, blocks: new Array(BLOCKS_PER_DAY).fill(0) })
  }

  async function applyAI() {
    if (!aiText.trim() || !day) return
    setAiBusy(true); setMsg('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const resp = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: 'parse-rest', text: aiText, date: activeDate, roster: rosterForDate }),
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || 'Parse failed')
      if (Array.isArray(data.blocks) && data.blocks.length === BLOCKS_PER_DAY) {
        updateDay({ ...day, blocks: data.blocks })
        setAiText('')
        setMsg('AI applied work periods. Check and adjust if needed.')
      } else {
        throw new Error('AI returned unexpected format')
      }
    } catch (e) {
      setMsg('AI error: ' + e.message)
    } finally { setAiBusy(false) }
  }

  function applyRosterRange() {
    if (!selectedCrew || !record) { alert('Pick a crew member first.'); return }
    const name = (getVal(selectedCrew['First Name']) + ' ' + getVal(selectedCrew['Last Name'])).trim().toLowerCase()
    const hits = []
    for (const d of dates) {
      for (const sc of schedules) {
        if (sc.date_from && d < sc.date_from) continue
        if (sc.date_to && d > sc.date_to) continue
        const row = (sc.grid || []).find(r => (r.crew_name || '').trim().toLowerCase() === name)
        if (row && typeof row.cells === 'string' && row.cells.length === 24) { hits.push({ date: d, cells: row.cells, schedule: sc.name }); break }
      }
    }
    if (!hits.length) { alert('No saved watch schedule covers this month for this crew member.'); return }
    const ok = window.confirm(
      'Pre-fill ' + hits.length + ' day' + (hits.length > 1 ? 's' : '') + ' from the roster?\n\n' +
      'This is the PLAN, not a record of what happened. Each day will be marked unconfirmed. ' +
      'You must check every day and correct anything that differed before signing.')
    if (!ok) return

    setRecord(r => {
      const days = { ...r.days }
      for (const h of hits) {
        const blocks = new Array(BLOCKS_PER_DAY).fill(0)
        h.cells.split('').forEach((c, hr) => { if (c === 'W') { blocks[hr * 2] = 1; blocks[hr * 2 + 1] = 1 } })
        const prevDay = days[h.date] || emptyDay()
        days[h.date] = { ...prevDay, blocks, fromPlan: true, confirmed: false,
          comment: prevDay.comment || 'Pre-filled from roster: ' + h.schedule + ' - not yet confirmed' }
      }
      return { ...r, days }
    })
    setMsg('Pre-filled ' + hits.length + ' days from the roster. Check each day before signing.')
  }

  async function save() {
    if (!record) return
    setSaving(true); setMsg('')
    try {
      const body = {
        'Crew Member': record.crewName,
        'Crew ID': getVal(selectedCrew['Crew ID']),
        'Position': getVal(selectedCrew['Position']) || getVal(selectedCrew['Role']),
        'Month': month,
        'Vessel': config?.vessel_name || '',
        'Days': JSON.stringify(record.days),
        'Status': 'Open',
        'Onboard': true,
      }
      if (record.rowId) {
        await updateRow('hours_rest', record.rowId, body)
      } else {
        const created = await createRow('hours_rest', body)
        setRecord(r => ({ ...r, rowId: created.id }))
      }
      setMsg('Saved ✓')
    } catch (e) {
      setMsg('Save failed: ' + e.message)
    } finally { setSaving(false) }
  }

  function generateReport() { setShowReport(true) }
  const hrs = day ? dayHours(day.blocks) : { work: 0, rest: 24 }
  const compliance = day && day.status === 'Onboard' ? evaluateDay(day.blocks, record && activeDate ? buildTimeline(record.days, activeDate) : null) : null
  const sevenDay = (record && activeDate) ? rollingSevenDayRest(record.days, activeDate) : null
  const rolling24 = (record && activeDate && day && day.status === 'Onboard') ? evaluateRolling24(record.days, activeDate) : null

  // ---------- render ----------
  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto' }}>

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={lbl}>Crew Member</label>
          <select style={sel} value={selectedCrew ? selectedCrew.id : ''} onChange={e => {
            const c = crewList.find(x => String(x.id) === e.target.value)
            setSelectedCrew(c || null)
          }}>
            <option value="">Select crew…</option>
            {crewList.map(c => (
              <option key={c.id} value={c.id}>
                {getVal(c['First Name'])} {getVal(c['Last Name'])} — {getVal(c['Position']) || getVal(c['Role'])}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={lbl}>Month</label>
          <input type="month" value={month} onChange={e => { monthTouched.current = true; setMonth(e.target.value) }}
            style={{ padding: '9px 12px', borderRadius: 9, border: '1px solid var(--border)',
              background: 'var(--card, #fff)', color: 'var(--text)', fontSize: 13, fontWeight: 500,
              boxShadow: '0 1px 2px rgba(10,22,40,.04)', minWidth: 150, cursor: 'pointer' }} />
        </div>
        {record && (
          <>
            <button style={btnPrimary} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save Record'}
            </button>
            <button style={btnGhost} onClick={generateReport}>Generate Report (PDF)</button>
            <button style={btnGhost} onClick={openArchive}>Archive</button>
            {record.rowId && <HistoryLink table="hours_rest" rowId={record.rowId} style={{ alignSelf: 'center' }} />}
          </>
        )}
      </div>

      {msg && <div style={{ marginBottom: 12, color: 'var(--gold3)', fontSize: 13 }}>{msg}</div>}
      {loading && <div style={{ color: 'var(--muted)' }}>Loading…</div>}

      {showArchive && (
        <div style={{ background: 'var(--hull)', border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Saved Reports Archive</span>
            <button style={btnGhost} onClick={() => setShowArchive(false)}>Close</button>
          </div>
          {archiveLoading && <div style={{ color: 'var(--muted)', fontSize: 12 }}>Loading…</div>}
          {!archiveLoading && archiveRows.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 12 }}>No saved reports yet.</div>}
          {!archiveLoading && archiveRows.map(r => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '0.5px solid var(--border)', fontSize: 13 }}>
              <div>
                <div style={{ fontWeight: 500 }}>{getVal(r['Crew Member'])} — {getVal(r['Month'])}</div>
                <div style={{ color: 'var(--muted)', fontSize: 11 }}>{getVal(r['Status'])}</div>
              </div>
              <a href={fileHref(getVal(r['Report URL']))} target="_blank" rel="noreferrer" style={{ color: 'var(--gold3)', fontSize: 12 }}>View PDF</a>
            </div>
          ))}
        </div>
      )}

      {record && day && (
        <>
          {/* Day picker */}
          {/* wraps onto a second row instead of scrolling sideways (ipad: days 17-31 were hidden at 1024px) */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingBottom: 8, marginBottom: 16 }}>
            {dates.map(d => {
              const dnum = Number(d.split('-')[2])
              const isActive = d === activeDate
              const dd = record.days[d]
              const hasWork = dd && dd.blocks.some(b => b === 1)
              return (
                <button key={d} onClick={() => setActiveDate(d)} style={{
                  minWidth: 38, padding: '6px 4px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: '1px solid var(--border)',
                  background: isActive ? 'var(--gold)' : 'var(--hull)',
                  color: isActive ? '#000' : (hasWork ? 'var(--gold2)' : 'var(--muted)'),
                  borderBottom: (record?.days?.[d]?.fromPlan && !record?.days?.[d]?.confirmed) ? '2px solid #8a5c0a' : undefined,
                  fontWeight: isActive ? 700 : 400,
                }}>{dnum}</button>
              )
            })}
          </div>

          {/* Active day header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>{new Date(activeDate + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}</h3>
            <select style={sel} value={day.status} onChange={e => updateDay({ ...day, status: e.target.value })}>
              {LEAVE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          {day.status === 'Onboard' ? (
            <>
              {/* AI input */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input style={{ ...sel, flex: 1 }} placeholder='e.g. "worked 0800–1730 and 2200–2300 watch"'
                  value={aiText} onChange={e => setAiText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') applyAI() }} />
                <button style={btnPrimary} onClick={applyAI} disabled={aiBusy}>
                  {aiBusy ? 'Reading…' : 'AI Fill'}
                </button>
                <button onClick={applyRosterRange} title="Pre-fill every day this month that a saved roster covers"
                  style={{ marginLeft: 8, padding: '8px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                    border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted)' }}>
                  Apply roster
                </button>
                <button style={btnGhost} onClick={clearDay}>Clear</button>
              </div>

              {/* The 48-block grid: 24 hours, each split in two halves */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', gap: 2, marginBottom: 12 }}>
                {Array.from({ length: 24 }).map((_, h) => (
                  <div key={h} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 9, color: 'var(--muted)', marginBottom: 2 }}>{String(h).padStart(2, '0')}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {[0, 1].map(half => {
                        const idx = h * 2 + half
                        const worked = day.blocks[idx] === 1
                        return (
                          <div key={idx} onClick={() => toggleBlock(idx)}
                            title={blockToLabel(idx) + (worked ? ' — work' : ' — rest')}
                            style={{
                              height: 22, cursor: 'pointer', borderRadius: 2,
                              background: worked ? 'var(--gold)' : 'var(--hull)',
                              border: '1px solid var(--border)',
                            }} />
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Live readout */}
              <div style={{ display: 'flex', gap: 24, marginBottom: 12, fontSize: 14 }}>
                <span><strong>Work:</strong> {hrs.work.toFixed(1)}h</span>
                <span><strong>Rest:</strong> {hrs.rest.toFixed(1)}h</span>
              </div>

              {/* Compliance flags */}
              {compliance && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  <Flag ok={compliance.rule10h} label="≥10h rest / 24h" />
                  <Flag ok={compliance.rule6h} label="≥6h continuous" />
                  <Flag ok={compliance.rule2periods} label="≤2 rest periods" />
                  <Flag ok={compliance.rule14h} label="≤14h between rest" />
                  {rolling24 && <Flag ok={rolling24.ok} label="any rolling 24h OK" />}
                  {sevenDay && (sevenDay.complete
                    ? <Flag ok={sevenDay.totalRest >= 77} label={`≥77h / 7d (${sevenDay.totalRest.toFixed(1)}h)`} />
                    : <Flag neutral label={`≥77h / 7d (${sevenDay.totalRest.toFixed(1)}h — partial)`} />)}
                </div>
              )}

              {/* Comment */}
              <div>
                <label style={lbl}>Comment</label>
                <input style={{ ...sel, width: '100%' }} maxLength={256} value={day.comment}
                  onChange={e => updateDay({ ...day, comment: e.target.value })} />
              </div>
            </>
          ) : (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', background: 'var(--hull)', borderRadius: 8 }}>
              Marked as <strong style={{ color: 'var(--text)' }}>{day.status}</strong> — work hours not recorded for this day.
            </div>
          )}
        </>
      )}

      {showReport && record && (
        <HoursReportModal
          record={record}
          dates={dates}
          month={month}
          crewName={record.crewName}
          position={getVal(selectedCrew['Position']) || getVal(selectedCrew['Role']) || ''}
          config={config}
          autoArchive={showReport === 'archive'}
          onClose={() => setShowReport(false)}
        />
      )}
    </div>
  )
}

function sigCanvas(id) {
  return (el) => {
    if (el && !el._init) {
      el._init = true
      el.width = 240; el.height = 70
      const ctx = el.getContext('2d')
      ctx.strokeStyle = '#000'; ctx.lineWidth = 1.5; ctx.lineCap = 'round'
      let drawing = false
      el.onmousedown = e => { drawing = true; ctx.beginPath(); ctx.moveTo(e.offsetX, e.offsetY) }
      el.onmousemove = e => { if (!drawing) return; ctx.lineTo(e.offsetX, e.offsetY); ctx.stroke() }
      el.onmouseup = () => { drawing = false }
      el.onmouseleave = () => { drawing = false }
      el.ontouchstart = e => { e.preventDefault(); drawing = true; const t = e.touches[0]; const r = el.getBoundingClientRect(); ctx.beginPath(); ctx.moveTo(t.clientX-r.left, t.clientY-r.top) }
      el.ontouchmove = e => { e.preventDefault(); if (!drawing) return; const t = e.touches[0]; const r = el.getBoundingClientRect(); ctx.lineTo(t.clientX-r.left, t.clientY-r.top); ctx.stroke() }
      el.ontouchend = () => { drawing = false }
    }
  }
}

function HoursReportModal({ record, dates, month, crewName, position, config, onClose, autoArchive }) {
  const [archiving, setArchiving] = useState(false)
  // Master signature: only the Captain, or a Chief Officer with Full on Hours of Rest, and never on their own month
  // (the database enforces the same: sql/hor-master-sign.sql). The others see who signed, or that it is awaiting the Master.
  const [masterSig, setMasterSig] = useState(null)   // { by } | null = not signed | undefined while loading
  const [signErr, setSignErr] = useState('')
  const ownRow = !!config?.crewName && String(crewName || '').trim().toLowerCase() === String(config.crewName).trim().toLowerCase()
  const ms = horMasterState({ accountType: config?.accountType, companyView: !!config?._companyView, role: config?.role, permissions: config?.permissions }, ownRow)
  useEffect(() => {
    if (!record?.rowId) { setMasterSig(null); return }
    let live = true
    supabase.from('hours_rest').select('signed_by_master').eq('id', record.rowId).maybeSingle()
      .then(({ data }) => { if (live) setMasterSig(data && String(data.signed_by_master || '').trim() ? { by: data.signed_by_master } : null) }, () => {})
    return () => { live = false }
  }, [record?.rowId])
  async function signAsMaster() {
    setSignErr('')
    const name = (config?.crewName || '').trim() || 'Master'
    try { await updateRow('hours_rest', record.rowId, { 'Signed by Master': name }); setMasterSig({ by: name }) }
    catch (e) { setSignErr(e.message) }
  }
  // Save to Archive: the PDF of this report view -> storage, then the month record gets Status Submitted + the link
  async function archiveReport() {
    setArchiving(true)
    try {
      const el = document.getElementById('hor-print')
      const np = el.querySelectorAll('.no-print')
      np.forEach(e => e.style.display = 'none')
      const opt = { margin: 6, filename: 'hor.pdf', image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' } }
      const blob = await html2pdf().set(opt).from(el).outputPdf('blob')
      np.forEach(e => e.style.display = '')
      const path = filePath('hours_of_rest', config?.id, month + '_' + crewName + '.pdf')
      const { error: upErr } = await supabase.storage.from('crew-docs').upload(path, blob, { contentType: 'application/pdf', upsert: false })
      if (upErr) throw new Error(upErr.message)
      // the month record in Supabase: Submitted + link to the PDF (the month must have been saved once)
      if (record.rowId) {
        await updateRow('hours_rest', record.rowId, { 'Status': 'Submitted', 'Report URL': path })   // private bucket: the record keeps the PATH, opened through a signed link
      } else {
        throw new Error('save the month first (Save), then Save to Archive')
      }
      alert('Report saved to archive!')
    } catch(e) {
      alert('Failed to save: ' + e.message)
    } finally { setArchiving(false) }
  }
  // opened by the AI with "archive": once, after the report view has been drawn
  const autoDone = useRef(false)
  useEffect(() => {
    if (!autoArchive) return
    const t = setTimeout(() => { if (autoDone.current) return; autoDone.current = true; archiveReport() }, 800)
    return () => clearTimeout(t)
  }, [autoArchive])
  const tz = config?.timezone || null
  const shipDate = shipTodayOr(tz)
  const today = new Date(shipDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    + (tz ? ` (ship's time \u00b7 ${tz})` : '')
  // If the recorded days carry different timezone stamps, the ship's clock
  // changed during this month. The legal record must say so, not hide it.
  const tzStamps = [...new Set(Object.values(record?.days || {}).map(d => d?.tz).filter(Boolean))]
  const tzChangeNote = tzStamps.length > 1
    ? `Ship's time changed during this month (${tzStamps.join(' \u2192 ')}). Daily rest hours are recorded against the ship's clock in force on each day.`
    : null
  const cellBorder = '0.5px solid #E5E9F1'

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '20px 0' }}>
      <div id="hor-print" style={{ background: '#fff', color: '#16233B', width: 1100, borderRadius: 8, padding: 30, fontFamily: 'Inter, Helvetica, Arial, sans-serif', colorScheme: 'light' }}>
        {tzChangeNote && (
          <div style={{ border: '1px solid #E5C687', background: '#FDF8EC', color: '#7A5C1E', borderRadius: 8, padding: '9px 14px', fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>
            {tzChangeNote}
          </div>
        )}

        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          <button onClick={() => {
            const el = document.getElementById('hor-print')
            const np = el.querySelectorAll('.no-print')
            np.forEach(e => e.style.display = 'none')
            const opt = { margin: 6, filename: `${month}_${crewName.replace(/\s+/g,'_')}_Hours_of_Rest.pdf`, image: { type: 'jpeg', quality: 0.98 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' } }
            html2pdf().set(opt).from(el).save().then(() => np.forEach(e => e.style.display = ''))
          }} style={{ padding: '6px 16px', background: '#1a56db', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Export to PDF</button>
          <button onClick={() => window.print()} style={{ padding: '6px 16px', background: '#6b7280', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Print</button>
          <button disabled={archiving} onClick={archiveReport} style={{ padding: '6px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            {archiving ? 'Saving...' : 'Save to Archive'}
          </button>
          <button onClick={onClose} style={{ padding: '6px 16px', background: '#e5e7eb', color: '#16233B', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Close</button>
        </div>

        {/* HEYCAPTAIN letterhead */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '3px solid #C9A84C', paddingBottom: 9, marginBottom: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ display: 'inline-block', fontSize: 30, fontWeight: 900, color: '#1155F5', lineHeight: 1, letterSpacing: -3 }}>{'\u2248'}</span>
              <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '.13em', color: '#0A1628' }}>HEYCAPTAIN</span>
            </div>
            <div style={{ fontSize: 9, color: '#67748C', letterSpacing: '.2em', textTransform: 'uppercase', marginTop: 3, marginLeft: 42 }}>Record of Hours of Work / Rest</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 9.5, color: '#67748C', lineHeight: 1.7 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#16233B' }}>{config?.vessel_name || 'Vessel'}</div>
            <div>Maritime Labour Convention 2006 &middot; Standard A2.3</div>
          </div>
        </div>
        <div style={{ background: '#0A1628', color: '#fff', padding: '6px 11px', fontSize: 10, letterSpacing: '.18em', textTransform: 'uppercase', margin: '0 0 14px' }}>
          {crewName} &middot; {position} &middot; {month}
        </div>

        <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #E5E9F1', boxShadow: '0 1px 2px rgba(10,22,40,.04)', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, border: 'none' }}>
          <tbody>
            <tr>
              <td style={{ border: cellBorder, padding: '4px 8px', fontWeight: 700, background: '#F6F8FB', width: '15%', color: '#16233B' }}>Seafarer</td>
              <td style={{ border: cellBorder, padding: '4px 8px', width: '35%', color: '#16233B' }}>{crewName}</td>
              <td style={{ border: cellBorder, padding: '4px 8px', fontWeight: 700, background: '#F6F8FB', width: '15%', color: '#16233B' }}>Vessel</td>
              <td style={{ border: cellBorder, padding: '4px 8px', width: '35%', color: '#16233B' }}>{config?.vessel_name || ''}</td>
            </tr>
            <tr>
              <td style={{ border: cellBorder, padding: '4px 8px', fontWeight: 700, background: '#F6F8FB', color: '#16233B' }}>Position</td>
              <td style={{ border: cellBorder, padding: '4px 8px', color: '#16233B' }}>{position}</td>
              <td style={{ border: cellBorder, padding: '4px 8px', fontWeight: 700, background: '#F6F8FB', color: '#16233B' }}>Period</td>
              <td style={{ border: cellBorder, padding: '4px 8px', color: '#16233B' }}>{month}</td>
            </tr>
          </tbody>
        </table>
        </div>

        <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #E5E9F1', boxShadow: '0 1px 2px rgba(10,22,40,.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, tableLayout: 'fixed', border: 'none', color: '#16233B' }}>
          <thead>
            <tr style={{ background: '#16314f', color: '#fff' }}>
              <th style={{ border: cellBorder, padding: '4px 6px', width: 64 }}>Date</th>
              {Array.from({length:24}).map((_,h)=>(
                <th key={h} colSpan={2} style={{ border: cellBorder, fontSize: 7, padding: '4px 0' }}>{String(h).padStart(2,'0')}</th>
              ))}
              <th style={{ border: cellBorder, fontSize: 8, width: 50, color: '#fff' }}>Work</th>
              <th style={{ border: cellBorder, fontSize: 8, width: 50, color: '#fff' }}>Rest</th>
              <th style={{ border: cellBorder, fontSize: 8, width: 56, color: '#fff' }}>Rest /7d</th>
            </tr>
          </thead>
          <tbody>
            {dates.map((d,i) => {
              const hasRecord = !!record.days[d]
              const dd = record.days[d] || { blocks: new Array(48).fill(0), status: 'Onboard', comment: '' }
              const onboard = !dd.status || dd.status === 'Onboard'
              const work = dd.blocks.reduce((a,b)=>a+(b?1:0),0)*0.5
              const rest = 24 - work
              const r24 = onboard ? evaluateRolling24(record.days, d) : { ok: true }
              const s7 = rollingSevenDayRest(record.days, d)
              const zebra = i % 2 === 0 ? '#fff' : '#FAFBFD'
              const dlabel = new Date(d+'T12:00:00').toLocaleDateString('en-GB',{weekday:'short'}) + ' ' + d.split('-')[2]
              return (
                <tr key={d} style={{ background: zebra }}>
                  <td style={{ border: cellBorder, padding: '3px 6px', fontWeight: 700, whiteSpace: 'nowrap', color: '#16233B', fontSize: 9 }}>{dlabel}</td>
                  {dd.blocks.map((b,bi)=>(
                    <td key={bi} style={{ border: '0.5px solid #EDF1F6', padding: 0, height: 17, background: b ? 'rgba(17,85,245,.75)' : zebra }} />
                  ))}
                  <td style={{ border: cellBorder, textAlign: 'center', fontSize: 9, color: hasRecord ? '#16233B' : '#C3CAD6' }}>{!hasRecord ? '\u2014' : (onboard ? work.toFixed(1) : '0.0')}</td>
                  <td style={{ border: cellBorder, textAlign: 'center', fontSize: 9, whiteSpace: 'nowrap',
                    background: (onboard && !r24.ok) ? 'rgba(201,59,71,.10)' : 'inherit',
                    color: (onboard && !r24.ok) ? '#C93B47' : '#16233B',
                    fontWeight: (onboard && !r24.ok) ? 700 : 400 }}>{!hasRecord ? '\u2014' : (onboard ? rest.toFixed(1) : '24.0')}</td>
                  <td style={{ border: cellBorder, textAlign: 'center', fontSize: 9, whiteSpace: 'nowrap',
                    background: (s7.complete && s7.totalRest < 77) ? '#fbdcdc' : 'inherit',
                    color: (s7.complete && s7.totalRest < 77) ? '#b3261e' : '#16233B',
                    fontWeight: (s7.complete && s7.totalRest < 77) ? 700 : 400 }}>{s7.daysCounted === 0 ? '\u2014' : s7.totalRest.toFixed(1) + (s7.complete ? '' : '*')}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        </div>

        <div style={{ fontSize: 8.5, color: '#5b6878', marginTop: 8, lineHeight: 1.6 }}>
          Shaded = hours of work &middot; Red rest cell = rolling 24h rest-rule deviation &middot; Red 7-day cell = below the 77h minimum &middot; * = partial 7-day window
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 30, fontSize: 11 }}>
          <div>
            <div style={{ marginBottom: 4 }}>Seafarer: <strong>{crewName}</strong></div>
            <div style={{ position: 'relative' }}>
              <canvas ref={sigCanvas('sig-seafarer')} id="sig-seafarer" style={{ border: cellBorder, cursor: 'crosshair', display: 'block' }} />
              <button onClick={() => { const c = document.getElementById('sig-seafarer'); c.getContext('2d').clearRect(0,0,c.width,c.height) }} className="no-print" style={{ position:'absolute', top:2, right:2, background:'transparent', border:'none', cursor:'pointer', fontSize:10, color:'#5b6878' }}>Clear</button>
            </div>
            <div style={{ fontSize: 10, marginTop: 3, color: '#555' }}>Date: {today}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ marginBottom: 4 }}>Master / Captain</div>
            <div style={{ position: 'relative', ...(ms.canSign ? {} : { opacity: .55, pointerEvents: 'none' }) }} aria-disabled={!ms.canSign}>
              <canvas ref={sigCanvas('sig-master')} id="sig-master" style={{ border: cellBorder, cursor: 'crosshair', display: 'block' }} />
              {ms.canSign && <button onClick={() => { const c = document.getElementById('sig-master'); c.getContext('2d').clearRect(0,0,c.width,c.height) }} className="no-print" style={{ position:'absolute', top:2, right:2, fontSize:9, padding:'1px 5px', cursor:'pointer' }}>Clear</button>}
            </div>
            {masterSig && <div style={{ fontSize: 10, marginTop: 3, color: '#555' }}>Signed as Master: <strong>{masterSig.by}</strong></div>}
            {!masterSig && !ms.canSign && <div className="no-print" style={{ fontSize: 10, marginTop: 3, color: '#5b6878' }}>{ms.own ? 'Your own month is signed by the Captain.' : 'Awaiting the Master (Captain, or Chief Officer with Full).'}</div>}
            {ms.canSign && !masterSig && record?.rowId && <button className="no-print" onClick={signAsMaster} style={{ marginTop: 6, fontSize: 11, padding: '4px 10px', cursor: 'pointer' }}>Sign as Master</button>}
            {signErr && <div className="no-print" role="alert" style={{ fontSize: 10, color: '#C93B47' }}>{signErr}</div>}
            <div style={{ fontSize: 10, marginTop: 3, color: '#555' }}>Date: {today}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function Flag({ ok, label, neutral }) {
  const bg = neutral ? 'rgba(150,150,150,.15)' : (ok ? 'rgba(61,158,114,.15)' : 'rgba(192,67,74,.15)')
  const color = neutral ? 'var(--muted)' : (ok ? '#137050' : '#b4313c')
  const mark = neutral ? '–' : (ok ? '✓' : '✕')
  return (
    <span style={{ fontSize: 12, padding: '4px 10px', borderRadius: 12, background: bg, color }}>{mark} {label}</span>
  )
}

const lbl = { display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }
const sel = {
  padding: '9px 34px 9px 12px', borderRadius: 9,
  border: '1px solid var(--border)', background: 'var(--card, #fff)',
  color: 'var(--text)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
  backgroundImage: 'url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'8\' viewBox=\'0 0 12 8\' fill=\'none\'%3E%3Cpath d=\'M1 1.5L6 6.5L11 1.5\' stroke=\'%2367748C\' stroke-width=\'1.6\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
  boxShadow: '0 1px 2px rgba(10,22,40,.04)',
  minWidth: 260,
}
const btnPrimary = { padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--gold)', color: '#16233B', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const btnGhost = { padding: '9px 16px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', fontSize: 13, cursor: 'pointer' }
